import type { PenaltyEnum, Rule } from "@b2bportal/ground-api";
import dayjs from "dayjs";
import type { TFunction } from "i18next";

/**
 * Finds a cancellation rule that applies to the same date window but has a different penalty type.
 *
 * In stepped cancellation policies, a single time window may have multiple penalty types
 * (e.g., both a FlatFee and a PercentageTotal). When this happens, the customer pays
 * whichever is lesser. This function helps identify such pairs so we can combine them
 * into a single user-friendly message.
 *
 * @param rules - All cancellation rules sorted chronologically
 * @param startIndex - Index of the current rule being processed
 * @param targetPenaltyType - The penalty type to search for (e.g., PenaltyEnum.FlatFee)
 * @param currentFrom - Start date of the current rule's time window
 * @param currentUntil - End date of the current rule's time window
 * @returns Index of the matching rule, or -1 if no match found
 *
 * @example
 * // Rules for the same date range but different penalties:
 * // Rule 1: FlatFee $50 from June 1 to June 10
 * // Rule 2: PercentageTotal 25% from June 1 to June 10
 * // These should be combined into: "Fee of $50 or 25%, whichever is lesser"
 */
export function findRuleWithMatchingDateWindow(
  rules: Rule[],
  startIndex: number,
  targetPenaltyType: PenaltyEnum,
  currentFrom: string,
  currentUntil: string
): number {
  return rules.findIndex(
    (r, i) =>
      i > startIndex &&
      r.penalty.Penalty === targetPenaltyType &&
      dayjs(r.from).isSame(currentFrom) &&
      dayjs(r.until).isSame(currentUntil)
  );
}

/**
 * Formats text for cancellation penalties where the customer pays the LESSER of two amounts.
 *
 * When a time window has both a FlatFee and a PercentageTotal penalty, the customer
 * is charged whichever amount is lower. This protects customers from excessive fees
 * while still allowing providers to set minimums or caps.
 *
 * @param feeAmount - The flat fee penalty amount in the booking currency
 * @param percentage - The percentage penalty (0-100, where 100 = total reservation cost)
 * @param formattedDate - The end date of this cancellation window (already formatted for display)
 * @param formatNumber - i18n number formatting function from useI18nContext
 * @param formatCurrency - i18n currency formatting function from useI18nContext
 * @param t - i18n translation function from useI18nContext
 * @returns Localized text describing the "whichever is lesser" penalty
 *
 * @example
 * // With 100% penalty: "Fee of $50 or total reservation amount, whichever is lesser"
 * formatLesserOfFeeOrPercentagePenalty(50, 100, "June 10", ...)
 *
 * @example
 * // With 25% penalty: "Fee of $50 or 25% of total price, whichever is lesser"
 * formatLesserOfFeeOrPercentagePenalty(50, 25, "June 10", ...)
 */
export function formatLesserOfFeeOrPercentagePenalty(
  feeAmount: number,
  percentage: number,
  formattedDate: string,
  formatNumber: (val: number, opts: Intl.NumberFormatOptions) => string,
  formatCurrency: (val: number, opts: Intl.NumberFormatOptions) => string,
  t: TFunction
): string {
  const formattedAmount = formatCurrency(feeAmount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  if (percentage === 100) {
    return t("core-cars.steppedCancellation.combinedFeeOrTotal", {
      amount: formattedAmount,
      date: formattedDate,
    });
  }

  const formattedPercentage = formatNumber(percentage / 100, {
    style: "percent",
  });

  return t("core-cars.steppedCancellation.combinedFeeOrPercentage", {
    amount: formattedAmount,
    percentage: formattedPercentage,
    date: formattedDate,
  });
}
